(function () {
  'use strict';

  var syncQueued = false;
  var actionDockRefreshTimer = null;
  var cartCache = null;
  var cartCacheAt = 0;
  var CART_CACHE_MS = 500;

  function scheduleAddQuantityToCollectionCards(root) {
    if (syncQueued) return;
    syncQueued = true;

    var run = function () {
      syncQueued = false;
      addQuantityToCollectionCards(root);
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run);
    } else {
      setTimeout(run, 16);
    }
  }

  function fetchCart(force) {
    var now = Date.now();
    if (!force && cartCache && now - cartCacheAt < CART_CACHE_MS) {
      return Promise.resolve(cartCache);
    }

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart fetch failed');
        return res.json();
      })
      .then(function (cart) {
        cartCache = cart;
        cartCacheAt = Date.now();
        return cart;
      });
  }

  function invalidateCartCache(cart) {
    cartCache = cart || null;
    cartCacheAt = cart ? Date.now() : 0;
  }

  function getVariantId(form) {
    var variantInput = form && form.querySelector('input[name="id"]');
    return variantInput && variantInput.value ? String(variantInput.value) : '';
  }

  function getCartLines(cart, variantId) {
    if (!cart || !cart.items || !variantId) return [];

    return cart.items
      .map(function (item, index) {
        return {
          item: item,
          line: index + 1,
        };
      })
      .filter(function (entry) {
        return entry.item && String(entry.item.variant_id) === String(variantId);
      });
  }

  function getLineQuantity(line) {
    return Math.max(0, parseInt(line && line.item && line.item.quantity, 10) || 0);
  }

  function getCartQuantity(cart, variantId) {
    return getCartLines(cart, variantId).reduce(function (total, line) {
      return total + getLineQuantity(line);
    }, 0);
  }

  function buildSplitLineUpdates(lines, targetQuantity) {
    var remaining = Math.max(0, parseInt(targetQuantity, 10) || 0);
    var updates = {};

    lines.forEach(function (line) {
      if (!line.item || !line.item.key) return;

      var nextQuantity = Math.min(getLineQuantity(line), remaining);
      updates[line.item.key] = nextQuantity;
      remaining -= nextQuantity;
    });

    return updates;
  }

  function hasSelectedSize(card, sizeButtons) {
    if (!sizeButtons.length) return true;

    return Array.prototype.some.call(sizeButtons, function (btn) {
      return (
        btn.classList.contains('active') ||
        btn.classList.contains('selected') ||
        btn.checked ||
        btn.getAttribute('aria-checked') === 'true'
      );
    });
  }

  function clampQuantity(value, min, max) {
    var numeric = parseInt(value, 10);
    if (!Number.isFinite(numeric)) numeric = min;
    return Math.max(min, Math.min(max, numeric));
  }

  function syncAllActionDocks(forceCart) {
    document.querySelectorAll('.ocs-card').forEach(function (card) {
      if (typeof card.__syncActionDockState === 'function') {
        card.__syncActionDockState(!!forceCart);
      }
    });
  }

  function refreshActionDocksFromCart() {
    invalidateCartCache();
    scheduleAddQuantityToCollectionCards();

    if (actionDockRefreshTimer) {
      clearTimeout(actionDockRefreshTimer);
    }

    actionDockRefreshTimer = setTimeout(function () {
      actionDockRefreshTimer = null;
      syncAllActionDocks(true);
    }, 80);
  }

  function addQuantityToCollectionCards(root) {
    // root may be an Element (scoped call from the recs rail) or an Event
    // (passed by the shopify:section:load / cart listeners) — only honor a real
    // DOM node, otherwise scan the whole document.
    var scope = (root && typeof root.querySelectorAll === 'function') ? root : document;
    var productCards = scope.querySelectorAll('.card--product, .product-card, .grid__item, .ocs-card');

    productCards.forEach(function (productCard) {
      // Only the compact OCS collection card gets the dock. Dropping the old
      // "|| productCard" fallback is what keeps this off the main PDP buy
      // button (it lives in a .grid__item but is not an .ocs-card).
      var card = productCard.classList.contains('ocs-card') ? productCard : productCard.querySelector('.ocs-card');
      if (!card) return;

      if (card.__collectionQtyInitialized) return;

      var form = card.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      var addButton = card.querySelector(
        'button[name="add"], .quick-add__submit, button[type="submit"], .product-form__submit'
      );
      if (!addButton) return;

      var sizeButtons = card.querySelectorAll(
        '.variant-button, .size-button, button[data-variant-id], input[type="radio"][name*="Size"]'
      );
      var dock = card.querySelector('[data-atc-dock], .ocs-atc-dock');

      if (!dock) {
        dock = document.createElement('div');
        dock.className = 'ocs-atc-dock';
        dock.setAttribute('data-atc-dock', '');
        addButton.parentElement.insertBefore(dock, addButton);
        dock.appendChild(addButton);
      }

      card.__collectionQtyInitialized = true;

      var qtySelector = dock.querySelector('.collection-quantity-selector');
      if (!qtySelector) {
        qtySelector = document.createElement('div');
        qtySelector.className = 'collection-quantity-selector';
        qtySelector.setAttribute('aria-live', 'polite');

        var minusBtn = document.createElement('button');
        minusBtn.type = 'button';
        minusBtn.className = 'ocs-qty-button ocs-qty-button--minus';
        minusBtn.innerHTML =
          '<svg width="14" height="2" viewBox="0 0 14 2" fill="currentColor" aria-hidden="true"><rect width="14" height="2" rx="1"/></svg>';
        minusBtn.setAttribute('aria-label', 'Decrease quantity');

        var qtyValue = document.createElement('span');
        qtyValue.className = 'ocs-qty-value';
        qtyValue.textContent = '1 IN CART';

        var qtyInput = document.createElement('input');
        qtyInput.type = 'number';
        qtyInput.name = 'quantity';
        qtyInput.value = '1';
        qtyInput.min = '1';
        qtyInput.max = '10';
        qtyInput.setAttribute('aria-label', 'Quantity');

        var plusBtn = document.createElement('button');
        plusBtn.type = 'button';
        plusBtn.className = 'ocs-qty-button ocs-qty-button--plus';
        plusBtn.innerHTML =
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 1.5v11M1.5 7h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
        plusBtn.setAttribute('aria-label', 'Increase quantity');

        qtySelector.appendChild(minusBtn);
        qtySelector.appendChild(qtyValue);
        qtySelector.appendChild(qtyInput);
        qtySelector.appendChild(plusBtn);
        dock.insertBefore(qtySelector, addButton);
      }

      var minusButton = qtySelector.querySelector('.ocs-qty-button--minus');
      var plusButton = qtySelector.querySelector('.ocs-qty-button--plus');
      var qtyLabel = qtySelector.querySelector('.ocs-qty-value');
      var qtyInputEl = qtySelector.querySelector('input[name="quantity"]');
      var quantityUpdateInFlight = false;

      function getReadyButtonLabel() {
        var variantButtons = card.querySelectorAll('.ocs-variant-circle[data-variant-id]');
        var selectedVariant = card.querySelector('.ocs-variant-circle.selected');
        var hasMultipleVariantChoices = variantButtons.length > 1;

        if (hasMultipleVariantChoices && selectedVariant && selectedVariant.dataset.variantTitle && selectedVariant.dataset.variantPrice) {
          return 'ADD ' + selectedVariant.dataset.variantTitle + ' • ' + selectedVariant.dataset.variantPrice;
        }

        return addButton.dataset.defaultLabel || 'ADD TO CART';
      }

      function restoreAddButton() {
        if (!getVariantId(form) || !hasSelectedSize(card, sizeButtons)) return;

        addButton.disabled = false;
        addButton.classList.remove('disabled');
        addButton.removeAttribute('aria-label');
        addButton.textContent = getReadyButtonLabel();
      }

      function blockAddButton(label) {
        addButton.disabled = true;
        addButton.classList.add('disabled');
        addButton.textContent = label;
      }

      function setDockInCart(quantity) {
        var qty = Math.max(1, parseInt(quantity, 10) || 1);
        var max = parseInt(qtyInputEl.max, 10);
        if (!Number.isFinite(max) || max < 1) max = 99;

        restoreAddButton();
        dock.classList.add('is-in-cart');
        qtyInputEl.value = String(qty);
        qtyLabel.textContent = qty + ' IN CART';
        minusButton.disabled = false;
        plusButton.disabled = qty >= max;
      }

      function setDockReady() {
        dock.classList.remove('is-in-cart');
        qtyInputEl.value = '1';
        qtyLabel.textContent = '1 IN CART';
        minusButton.disabled = false;
        plusButton.disabled = false;
        restoreAddButton();
      }

      async function applyInventoryMax(cart) {
        try {
          var variantId = getVariantId(form);
          if (!variantId) {
            qtyInputEl.max = '10';
            return;
          }

          var invEl = card.querySelector('.variant-inventory');
          var invMap = invEl ? JSON.parse(invEl.textContent) : null;
          var meta = invMap ? invMap[variantId] : null;

          var deny = (meta && meta.p ? meta.p : 'deny') === 'deny';
          var onHand = meta && Number.isFinite(meta.q) ? meta.q : null;
          if (!cart) {
            cart = await fetchCart(false);
          }
          var inCart = getCartQuantity(cart, variantId);

          if (deny && onHand !== null) {
            var remaining = Math.max(0, onHand - inCart);
            var cap = Math.max(inCart || 1, Math.min(onHand, 99));
            qtyInputEl.max = String(cap);
            plusButton.disabled = inCart >= cap || remaining === 0;

            if (onHand <= 0 && inCart <= 0) {
              blockAddButton('OUT OF STOCK');
            } else {
              restoreAddButton();
            }
          } else {
            qtyInputEl.max = '99';
            restoreAddButton();
          }
        } catch (e) {
          qtyInputEl.max = '10';
        }
      }

      async function syncActionDockState(forceCart) {
        var variantId = getVariantId(form);
        var readyForVariant = hasSelectedSize(card, sizeButtons);

        if (!variantId || !readyForVariant) {
          setDockReady();
          return;
        }

        try {
          var cart = await fetchCart(!!forceCart);
          await applyInventoryMax(cart);
          var quantity = getCartQuantity(cart, variantId);
          if (quantity > 0) {
            setDockInCart(quantity);
          } else {
            setDockReady();
          }
        } catch (e) {
          setDockReady();
        }
      }

      async function changeCartQuantity(nextQuantity) {
        var variantId = getVariantId(form);
        if (!variantId) return;
        if (quantityUpdateInFlight) return;

        var max = parseInt(qtyInputEl.max, 10);
        if (!Number.isFinite(max) || max < 1) max = 99;
        var target = Math.max(0, Math.min(max, parseInt(nextQuantity, 10) || 0));

        quantityUpdateInFlight = true;
        qtySelector.setAttribute('aria-busy', 'true');
        minusButton.disabled = true;
        plusButton.disabled = true;

        try {
          var currentCart = await fetchCart(true);
          var lines = getCartLines(currentCart, variantId);
          var currentTotal = lines.reduce(function (total, line) {
            return total + getLineQuantity(line);
          }, 0);
          var cart = currentCart;

          if (target > currentTotal) {
            var addResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              credentials: 'same-origin',
              body: JSON.stringify({
                id: variantId,
                quantity: target - currentTotal,
              }),
            });

            if (!addResponse.ok) throw new Error('Cart add failed');
            cart = await fetchCart(true);
          } else if (target < currentTotal) {
            if (!lines.length) throw new Error('Cart line not found');

            var updates = buildSplitLineUpdates(lines, target);
            if (!Object.keys(updates).length) throw new Error('Cart line key not found');

            var updateResponse = await fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              credentials: 'same-origin',
              body: JSON.stringify({ updates: updates }),
            });

            if (!updateResponse.ok) throw new Error('Cart update failed');
            cart = await updateResponse.json();
          }

          invalidateCartCache(cart);

          var quantity = getCartQuantity(cart, variantId);
          if (quantity > 0) {
            setDockInCart(quantity);
          } else {
            setDockReady();
          }

          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart, source: 'collection-quantity-selector' } }));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, source: 'collection-quantity-selector' } }));
        } catch (e) {
          qtySelector.classList.add('shake');
          setTimeout(function () {
            qtySelector.classList.remove('shake');
          }, 340);
          syncActionDockState(true);
        } finally {
          quantityUpdateInFlight = false;
          qtySelector.removeAttribute('aria-busy');
        }
      }

      qtySelector.addEventListener('click', function (e) {
        var qtyButton = e.target && e.target.closest ? e.target.closest('.ocs-qty-button') : null;
        if (!qtyButton || !qtySelector.contains(qtyButton)) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        if (quantityUpdateInFlight) return;

        if (qtyButton.classList.contains('ocs-qty-button--minus')) {
          var currentQty = clampQuantity(qtyInputEl.value, 1, 99);
          changeCartQuantity(currentQty - 1);
          return;
        }

        if (qtyButton.classList.contains('ocs-qty-button--plus')) {
          var max = parseInt(qtyInputEl.max, 10);
          if (!Number.isFinite(max) || max < 1) max = 99;
          var current = clampQuantity(qtyInputEl.value, 1, max);

          if (current >= max) {
            qtySelector.classList.add('shake');
            setTimeout(function () {
              qtySelector.classList.remove('shake');
            }, 340);
            return;
          }

          changeCartQuantity(current + 1);
        }
      }, true);

      sizeButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          setTimeout(function () {
            syncActionDockState(true);
          }, 0);
        });
        btn.addEventListener('change', function () {
          syncActionDockState(true);
        });
      });

      var submitBtnObserver = new MutationObserver(function () {
        var btnText = addButton.textContent.trim().toUpperCase();
        if (btnText.includes('CHOOSE SIZE') || btnText.includes('SELECT SIZE')) {
          dock.classList.remove('is-in-cart');
        }
      });

      submitBtnObserver.observe(addButton, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'disabled'],
      });

      card.__applyInventoryMax = applyInventoryMax;
      card.__syncActionDockState = function (forceCart) {
        void syncActionDockState(!!forceCart);
      };
      card.__setActionDockAdded = function (variantId) {
        if (String(variantId) !== getVariantId(form)) return;
        invalidateCartCache();
        void syncActionDockState(true);
      };

      void syncActionDockState(true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleAddQuantityToCollectionCards);
  } else {
    scheduleAddQuantityToCollectionCards();
  }

  // Public hook for surfaces that inject cards after load (AI recommendations
  // rail); addQuantityToCollectionCards is idempotent per card.
  window.refreshCollectionQuantityDocks = scheduleAddQuantityToCollectionCards;

  document.addEventListener('shopify:section:load', scheduleAddQuantityToCollectionCards);
  document.addEventListener('cart:refresh', function () {
    invalidateCartCache();
    syncAllActionDocks(true);
  });
  document.addEventListener('cart:updated', function (event) {
    invalidateCartCache(event.detail && event.detail.cart);
    syncAllActionDocks(true);
  });
  window.addEventListener('pageshow', refreshActionDocksFromCart);
  window.addEventListener('focus', refreshActionDocksFromCart);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      refreshActionDocksFromCart();
    }
  });

  var observer = new MutationObserver(function () {
    scheduleAddQuantityToCollectionCards();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
